<template>
  <!-- 报备记录Drawer -->
  <a-drawer
    :width="900"
    :visible="drawerVisible"
    :unmount-on-close="true"
    :mask-closable="false"
    hide-cancel
    ok-text="关闭"
    @ok="closeDrawer"
    @cancel="closeDrawer"
  >
    <template #title>
      <a-space>
        {{
          `${customerInfo?.nickname}的${$t('report.records')}` ||
          $t('report.records')
        }}
        <a-tooltip content="添加报备记录">
          <a-button type="primary" size="mini" @click="showAddModal">
            <template #icon>
              <icon-plus />
            </template>
            添加报备记录
          </a-button>
        </a-tooltip>
      </a-space>
    </template>

    <a-table
      :columns="reportColumns"
      :data="reportData"
      :pagination="reportPagination"
      :loading="loading"
      :scroll="{ x: 1600 }"
      @page-change="handlePageChange"
    >
      <template #index="{ rowIndex }">
        {{
          rowIndex +
          1 +
          (reportPagination.current - 1) * reportPagination.pageSize
        }}
      </template>
      <template #imageArray="{ record }">
        <a-image-preview-group>
          <a-space>
            <a-image
              v-for="image in record.imageArray"
              :key="image"
              width="32"
              :src="image"
            />
          </a-space>
        </a-image-preview-group>
      </template>
      <template #state="{ record }">
        <a-tag :color="getStateColor(record.state)">
          {{ getStateText(record.state) }}
        </a-tag>
      </template>
      <template #operations="{ record }">
        <a-button type="primary" size="mini" @click="handleMsgRecord(record)">
          {{ $t('form.columns.operations.msgRecord') }}
        </a-button>
        <!-- <a-button type="text" size="small">
          <icon-edit />
        </a-button> -->
      </template>
    </a-table>
  </a-drawer>

  <!-- 添加报备Modal -->
  <a-modal
    :visible="addReportVisible"
    :mask-closable="false"
    title="添加报备记录"
    unmount-on-close
    @before-ok="handleBeforeOk"
    @cancel="handleModalCancel"
  >
    <a-form :model="formData">
      <a-form-item field="name" :label="$t('form.name')">
        <a-input
          v-model="formData.name"
          :placeholder="$t('form.name.placeholder')"
        />
      </a-form-item>
      <a-form-item field="mobile" :label="$t('form.mobile')">
        <a-input
          v-model="formData.mobile"
          :placeholder="$t('form.mobile.placeholder')"
        />
      </a-form-item>
      <a-form-item field="project" :label="$t('report.project')">
        <a-input
          v-model="formData.project"
          :placeholder="$t('report.project.placeholder')"
        />
      </a-form-item>
      <a-form-item field="city" :label="$t('report.city')">
        <a-select
          v-model="formData.city"
          :options="cityOptions"
          placeholder="请选择城市"
          allow-search
          @change="handleCityChange"
        />
      </a-form-item>
      <a-form-item field="hospital" :label="$t('report.hospital')">
        <a-select
          v-model="formData.cid"
          :options="hospitalOptions"
          placeholder="请选择医院"
          allow-search
          multiple
          limit="5"
          @change="handleHospitalChange"
        />
      </a-form-item>
      <a-form-item field="msg" :label="$t('report.msg')">
        <a-input
          v-model="formData.msg"
          :placeholder="$t('report.msg.placeholder')"
        />
      </a-form-item>
      <a-form-item field="images" label="上传图片">
        <a-upload
          list-type="picture-card"
          tip="上传图片"
          :action="uploadImageUrl"
          :file-list="fileList"
          :on-before-upload="beforeUpload"
          :accept="['image/jpeg', 'image/png', 'image/gif', 'image/webp']"
          :limit="5"
          image-preview
          :headers="{ token: userStore.token }"
          @success="handleSuccess"
          @remove="handleImageRemove"
        />
      </a-form-item>
    </a-form>
  </a-modal>

  <ReportMsgDrawer
    :visible="msgDrawerVisible"
    :list="msgDrawerContent"
    @update:visible="msgDrawerVisible = $event"
  />
</template>

<script setup lang="ts">
  import { ref, reactive, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Message } from '@arco-design/web-vue';
  import {
    addReport,
    queryReportList,
    type AddReportParams,
    type ReportRecord,
    type ReportParams,
    queryCityList,
    queryHospitalList,
    type HospitalParams,
    type HospitalRecord,
    MessageInfo,
  } from '@/api/report';
  import type { CustomerRecord } from '@/api/followup';
  import { uploadImage, UploadImageParams } from '@/api/upload';
  import { useUserStore } from '@/store';
  import { stringToArray } from '@/utils/utils';
  import ReportMsgDrawer from '@/components/report-msg-drawer/index.vue';

  const userStore = useUserStore();

  // 报备状态枚举
  const ReportState = {
    PROCESSING: 1, // 待处理
    EXSIST: 2, // 重单
    EXSISTDEEP: 3, // 重单可深度
    NOTEXSIST: 4, // 不重单
  } as const;

  // 状态文本映射
  const stateTextMap: Record<number, string> = {
    [ReportState.PROCESSING]: '待处理',
    [ReportState.EXSIST]: '重单',
    [ReportState.EXSISTDEEP]: '重单可深度',
    [ReportState.NOTEXSIST]: '不重单',
  };

  // 状态颜色映射
  const stateColorMap: Record<number, string> = {
    [ReportState.PROCESSING]: 'blue',
    [ReportState.EXSIST]: 'orange',
    [ReportState.EXSISTDEEP]: 'green',
    [ReportState.NOTEXSIST]: 'red',
  };

  // 获取状态文本
  const getStateText = (state: number): string => {
    return stateTextMap[state] || '未知状态';
  };

  // 获取状态颜色
  const getStateColor = (state: number): string => {
    return stateColorMap[state] || 'gray';
  };

  interface Props {
    drawerVisible: boolean;
    customerInfo?: CustomerRecord;
  }

  interface Emits {
    (e: 'update:drawerVisible', value: boolean): void;
    (e: 'success'): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();
  const { t } = useI18n();

  // 添加报备Modal状态
  const addReportVisible = ref(false);
  const loading = ref(false);

  // 报备表格数据
  const reportData = ref<ReportRecord[]>([]);
  const reportPagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 表格列定义
  const reportColumns = computed(() => [
    {
      title: '序号',
      slotName: 'index',
      width: 50,
    },
    {
      title: t('form.name'),
      dataIndex: 'name',
      width: 80,
      ellipsis: true,
    },
    {
      title: t('form.mobile'),
      dataIndex: 'mobile',
      width: 100,
      ellipsis: true,
    },
    {
      title: t('report.state'),
      dataIndex: 'state',
      slotName: 'state',
      width: 100,
      ellipsis: true,
    },
    {
      title: t('report.project'),
      dataIndex: 'project',
      width: 120,
      ellipsis: true,
    },
    {
      title: t('report.city'),
      dataIndex: 'city',
      width: 100,
      ellipsis: true,
    },
    {
      title: t('report.hospital'),
      dataIndex: 'hospital_name',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('report.msg'),
      dataIndex: 'note.content',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('report.form.images'),
      dataIndex: 'imageArray',
      width: 120,
      slotName: 'imageArray',
    },
    {
      title: t('report.form.state.create_time'),
      dataIndex: 'times',
      width: 200,
      ellipsis: true,
    },
    {
      title: '操作',
      dataIndex: 'operations',
      slotName: 'operations',
      width: 100,
      fixed: 'right',
    },
  ]);

  // 添加报备表单数据
  const formData = reactive<AddReportParams>({
    cid: [],
    project: '',
    city: '',
    name: props.customerInfo?.nickname || '',
    mobile: props.customerInfo?.mobile || '',
    images: '',
    msg: '',
    is_privacy: 0,
    uid: 0,
    uuid: 0,
  });

  // 城市和医院选项
  const cityOptions = ref<{ label: string; value: string }[]>([]);
  const hospitalOptions = ref<{ label: string; value: number }[]>([]);

  // 图片上传相关
  const fileList = ref<
    Array<{
      uid: string;
      name: string;
      url?: string;
      status: 'done' | 'uploading' | 'error';
    }>
  >([]);

  const uploadImageUrl = `${
    import.meta.env.VITE_API_BASE_URL
  }/tool/upload_image`;

  const beforeUpload = (file: File) => {
    const isPic = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isPic) {
      Message.error('只能上传 JPG 或 PNG 格式的图片');
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      Message.error('图片大小不能超过 2MB');
    }
    return isPic && isLt2M;
  };

  const handleSuccess = (response: any) => {
    Message.success('上传成功');
    const file = {
      name: response.name,
      url: response.response.data.url,
    };
    // file.fullurl = response.response.code.fullurl
    // console.log('Response', response.response.code.fullurl)
    fileList.value.push(file as any);
  };

  // 更新formData.images字段
  const updateFormImages = () => {
    const uploadedImages = fileList.value
      .filter((file) => file.status === 'done' && file.url)
      .map((file) => file.url);

    formData.images = uploadedImages.join(',');
  };

  // 删除图片
  const handleImageRemove = (file: any) => {
    const fileIndex = fileList.value.findIndex((f) => f.uid === file.uid);
    if (fileIndex !== -1) {
      fileList.value.splice(fileIndex, 1);
      updateFormImages();
    }
  };

  // 获取城市列表
  const fetchCities = async () => {
    try {
      const res = await queryCityList();
      cityOptions.value = (res.data as unknown as string[]).map(
        (city: string) => ({
          label: city,
          value: city,
        })
      );
    } catch (error) {
      console.error('获取城市列表失败:', error);
    }
  };

  // 获取医院列表
  const fetchHospitals = async (city: string) => {
    if (!city) {
      hospitalOptions.value = [];
      return;
    }

    try {
      const params: HospitalParams = { city };
      const res = await queryHospitalList(params);
      hospitalOptions.value = (res.data as unknown as HospitalRecord[]).map(
        (hospital: HospitalRecord) => ({
          label: hospital.nickname,
          value: hospital.id,
        })
      );
    } catch (error) {
      console.error('获取医院列表失败:', error);
      hospitalOptions.value = [];
    }
  };

  // 获取报备记录列表
  const fetchReportList = async () => {
    if (!props.customerInfo?.user_id) return;

    loading.value = true;
    try {
      const params: ReportParams = {
        state: 0,
        page: reportPagination.current,
        pageSize: reportPagination.pageSize,
        mobile: props.customerInfo.mobile,
      };
      const res = await queryReportList(params);
      // reportData.value = res.data.list;
      reportData.value = res.data.list.map((item) => {
        // item.imageArray = stringToArray(item.images);
        item.imageArray = stringToArray(item.msg_images);
        return item;
      });
      reportPagination.total = res.data.total;
    } catch (error) {
      console.error('获取报备记录失败:', error);
    } finally {
      loading.value = false;
    }
  };

  // 事件处理函数
  const closeDrawer = () => {
    // 清空数据
    reportData.value = [];
    reportPagination.current = 1;
    reportPagination.total = 0;
    emit('update:drawerVisible', false);
  };

  const showAddModal = () => {
    // 重置表单数据为当前用户信息
    formData.name = props.customerInfo?.nickname || '';
    formData.mobile = props.customerInfo?.mobile || '';
    addReportVisible.value = true;
    fetchCities();
  };

  const resetForm = () => {
    Object.assign(formData, {
      cid: [],
      project: '',
      city: '',
      name: props.customerInfo?.nickname || '',
      mobile: props.customerInfo?.mobile || '',
      images: '',
      msg: '',
      is_privacy: 0,
      uid: 0,
      uuid: 0,
    });
    cityOptions.value = [];
    hospitalOptions.value = [];
    fileList.value = [];
  };

  const handleModalCancel = () => {
    resetForm();
    addReportVisible.value = false;
  };

  const handleCityChange = (city: string) => {
    formData.city = city;
    formData.cid = [];
    fetchHospitals(city);
  };

  const handleHospitalChange = (hospitalIds: number[]) => {
    formData.cid = hospitalIds;
  };

  const handlePageChange = (page: number) => {
    reportPagination.current = page;
    fetchReportList();
  };

  const handleBeforeOk = async (done: (closed: boolean) => void) => {
    // 验证客户信息
    if (!props.customerInfo?.user_id) {
      Message.error('客户信息不完整');
      done(false); // 阻止关闭
      return;
    }

    // 验证表单必填项
    if (
      !formData.name ||
      !formData.mobile ||
      !formData.project ||
      !formData.city ||
      formData.cid.length === 0
    ) {
      Message.error('请填写完整信息');
      done(false); // 阻止关闭
      return;
    }

    try {
      const params: AddReportParams = {
        ...formData,
        uid: props.customerInfo.user_id,
        uuid: props.customerInfo.user_id,
      };

      await addReport(params);
      Message.success('添加报备记录成功');
      resetForm();
      fetchReportList();
      emit('success');
      done(true); // 允许关闭
      handleModalCancel();
    } catch (error) {
      console.error('添加报备记录失败:', error);
      Message.error('添加报备记录失败');
      done(false); // 阻止关闭
    }
  };

  const msgDrawerVisible = ref(false);
  const msgDrawerContent = ref<Array<MessageInfo>>([]);

  const handleMsgRecord = (record: ReportRecord) => {
    console.log('msg list: ', record.msg_list);
    msgDrawerContent.value = record.msg_list;
    msgDrawerVisible.value = true;
    // drawerContent.value = record;
    // drawerVisible.value = true;
  };

  // 监听drawer显示状态，获取报备记录
  watch(
    () => props.drawerVisible,
    (visible) => {
      if (visible && props.customerInfo?.user_id) {
        fetchReportList();
      }
    },
    { immediate: true }
  );
</script>

<style scoped>
  /* 可以添加一些样式 */
</style>
